import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  getVacancy,
  getVacancies,
  getVacancyCount,
  getVacancyFilterOptions,
} from '@services/vacancy.service';
import { getAddressSugestions } from '@services/geocoding.service';
import { getUserCompanies, getUserDetails } from '@services/user.service';
import { setSearchGeometry } from '@App/components/Map/map.slice';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';
import { useMemo } from 'react';
import { bbox } from '@turf/turf';
import { getFietssnelwegen } from './fietssnelwegen.service';
import {
  getNearbyStops,
  getRoute,
  getStop,
  getStopsForBbox,
} from './otp.service';
import { getCompanyMobilityOffer, getCompanySuggestions } from './company.service';
import { getCompanyUsers } from './admin.service';

function useVacancies(locationDetails, mode, date, time) {
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ['vacancies', locationDetails, mode, date, time],
    queryFn: async () => {
      const { searchGeometry, vacancies } = await getVacancies(locationDetails, mode, date, time);

      dispatch(setSearchGeometry(searchGeometry));

      return vacancies;
    },
    enabled: !!(locationDetails?.lat && locationDetails?.lng),
  });
}

function useVacancyCount() {
  return useQuery({
    queryKey: ['vacancyCount'],
    queryFn: async () => {
      const { count } = await getVacancyCount();

      return count;
    },
  });
}

function useVacancy(OIDN) {
  return useQuery({
    queryKey: ['vacancy', OIDN],
    queryFn: async () => getVacancy(OIDN),
    enabled: !!(OIDN),
  });
}

function useAddressSugestions(query) {
  return useQuery({
    queryKey: ['addressSugestions', query],
    queryFn: async () => getAddressSugestions(query),
    enabled: !!(query && query.length >= 3),
    placeholderData: query.length > 3 ? keepPreviousData : [],
  });
}

function useUserDetails(enabled = true) {
  return useQuery({
    queryKey: ['user', 'user-details'],
    queryFn: getUserDetails,
    enabled,
  });
}

function useFietssnelwegen() {
  return useQuery({
    queryKey: ['fietssnelwegen'],
    queryFn: getFietssnelwegen,
  });
}

function useVacancyFilterOptions() {
  return useQuery({
    queryKey: ['vacancies', 'filter'],
    queryFn: getVacancyFilterOptions,
  });
}

function useNearbyStops(lat, lon) {
  return useQuery({
    queryKey: ['nearby-stops', lat, lon],
    queryFn: async () => getNearbyStops(lat, lon),
    enabled: !!lat && !!lon,
    initialData: { nmbs: [], delijn: [] },
  });
}

function useStopsForBbox(minLat, minLon, maxLat, maxLon, enabled = true) {
  return useQuery({
    queryKey: ['stops', minLat, minLon, maxLat, maxLon],
    queryFn: async () => getStopsForBbox(minLat, minLon, maxLat, maxLon),
    enabled,
    placeholderData: keepPreviousData,
  });
}

function useRoute(from, to, mode, dateTime = undefined) {
  return useQuery({
    queryKey: ['route', from, to, mode, dateTime],
    queryFn: async () => getRoute(from, to, mode, dateTime),
    enabled: !!from?.lat && !!from?.lng && !!to?.lat && !!to?.lng && !!mode,
  });
}

function useUserCompanies() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user', 'companies', user],
    queryFn: getUserCompanies,
    enabled: !!user && user.groups?.includes('employers'),
  });
}

function useCompanySuggestions(inputValue, searchGeometry) {
  const searchGeometryBbox = useMemo(() => {
    try {
      return bbox(searchGeometry);
    } catch (err) {
      return undefined;
    }
  }, [searchGeometry]);

  return useQuery({
    queryKey: ['company-suggestions', inputValue, searchGeometryBbox],
    queryFn: () => {
      return getCompanySuggestions(inputValue, searchGeometryBbox);
    },
    enabled: !!inputValue && inputValue.length > 2 && !!searchGeometryBbox,
  });
}

function useCompanyMobilityOffer(companyId) {
  return useQuery({
    queryKey: ['company', 'mobility-offer', companyId],
    queryFn: () => getCompanyMobilityOffer(companyId),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

function useStop(id) {
  return useQuery({
    queryKey: ['stop', id],
    queryFn: () => getStop(id),
    enabled: !!id,
  });
}

function useCompanyUsers(id) {
  return useQuery({
    queryKey: ['company', 'users', id],
    queryFn: () => getCompanyUsers(id),
    enabled: !!id,
  });
}

export {
  useAddressSugestions,
  useCompanyMobilityOffer,
  useCompanySuggestions,
  useCompanyUsers,
  useFietssnelwegen,
  useNearbyStops,
  useRoute,
  useStop,
  useStopsForBbox,
  useUserCompanies,
  useUserDetails,
  useVacancies,
  useVacancy,
  useVacancyCount,
  useVacancyFilterOptions,
};
